import Component, { ComponentBackgroundVariants } from 'src/helpers/Component/Component';
import { Feature } from 'src/.generated/Feature.EnterpriseWeb.model';
import {
  MediaPrimary,
  MediaPrimaryStaticProps,
  getMediaPrimaryStaticProps,
} from 'src/helpers/Media';
import Headline from 'src/helpers/Headline/Headline';
import {
  GetStaticComponentProps,
  LinkField,
  useComponentProps,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { useTheme } from 'lib/context/ThemeContext';
import { PromoGenericTheme } from './PromoGeneric.theme';
import { Eyebrow } from 'src/helpers/Eyebrow';
import { BodyCopy } from 'src/helpers/BodyCopy';
import { Button } from 'src/helpers/Button';
import { RichTextWrapper } from 'src/helpers/RichTextWrapper';
import PriceLevel from 'src/helpers/PriceLevel/PriceLevel';
import { getEnum } from 'lib/utils/get-enum';
import LinkWrapper from 'src/helpers/LinkWrapper/LinkWrapper';

export type PromoGenericProps =
  Feature.EnterpriseWeb.Enterprise.Components.Promo.PromoGeneric.PromoGeneric;

const PromoGeneric = (props: PromoGenericProps): JSX.Element => {
  const { themeData } = useTheme(
    PromoGenericTheme(
      props?.fields?.imgPosition,
      props?.fields?.imageRatio,
      props?.fields?.useLegalCopyFont?.value || false
    )
  );
  const priceLevel = parseInt(props.fields?.priceLevel?.fields.priceLevelText?.value || '');

  const hasCta1Link =
    props?.fields?.cta1Link?.value?.href || props?.fields?.cta1ModalLinkText?.value;
  const hasCta2Link =
    props?.fields?.cta2Link?.value?.href || props?.fields?.cta2ModalLinkText?.value;

  const style = getEnum<ComponentBackgroundVariants>(props.fields?.backgroundColor) ?? 'white';
  const componentProps = useComponentProps<MediaPrimaryStaticProps>(props.rendering.uid);

  const CTALinkWrapper: React.FC<{ link?: LinkField }> = ({ children, link }): JSX.Element =>
    link?.value?.href ? (
      <LinkWrapper
        field={link?.value}
        ariaLabel={{ value: link?.value?.text || 'CTA Link' }}
        suppressLinkText
      >
        {children}
      </LinkWrapper>
    ) : (
      <>{children}</>
    );

  return (
    <Component
      variant="full"
      gap="gap-x-0"
      padding="px-0"
      backgroundVariant={style}
      dataComponent="general/promogeneric"
      {...props}
    >
      <div className="col-span-12">
        <div className="md:max-w-screen-lg lg:mx-auto">
          <div className={themeData.classes.wrapperClass}>
            <div className={themeData.classes.txtDivClass}>
              <div>
                <Eyebrow classes={themeData.classes.eyebrowClass} {...props} />
                <Headline classes={themeData.classes.headlineClass} {...props} />
                {priceLevel ? (
                  <div className={themeData.classes.priceLevelWrapper}>
                    <PriceLevel
                      priceLevel={priceLevel}
                      priceClasses={themeData.classes.priceTextClasses}
                      priceLevelClasses={themeData.classes.priceLevelClasses}
                    />
                  </div>
                ) : (
                  <></>
                )}
                <BodyCopy classes={themeData.classes.bodyClass} {...props} />
                {(hasCta1Link || hasCta2Link) && (
                  <div className="flex flex-col space-y-2 md:mb-5 md:flex-row md:items-baseline md:space-x-5">
                    {hasCta1Link && (
                      <Button
                        field={props?.fields?.cta1Link}
                        variant={props?.fields?.cta1Style}
                        icon={props?.fields?.cta1Icon}
                        modalId={
                          (
                            props?.fields
                              ?.cta1Modal as unknown as Feature.EnterpriseWeb.Enterprise.Components.Modal.GenericModal.GenericModal
                          )?.fields?.modalId?.value
                        }
                        modalLinkText={props?.fields?.cta1ModalLinkText}
                        classes=""
                      />
                    )}
                    {hasCta2Link && (
                      <Button
                        field={props?.fields?.cta2Link}
                        variant={props?.fields?.cta2Style}
                        icon={props?.fields?.cta2Icon}
                        modalId={
                          (
                            props?.fields
                              ?.cta2Modal as unknown as Feature.EnterpriseWeb.Enterprise.Components.Modal.GenericModal.GenericModal
                          )?.fields?.modalId?.value
                        }
                        modalLinkText={props?.fields?.cta2ModalLinkText}
                        classes=""
                      />
                    )}
                  </div>
                )}
                {props.fields?.bottomCaptionHeadline.value ||
                props.fields?.bottomCaptionDescription.value ? (
                  <div className="items-bottom">
                    <RichTextWrapper
                      classes={themeData.classes.bottomHeadingClass}
                      field={props.fields?.bottomCaptionHeadline}
                    />
                    <RichTextWrapper
                      classes={themeData.classes.bottomDescriptionClass}
                      field={props.fields?.bottomCaptionDescription}
                    />
                  </div>
                ) : (
                  <></>
                )}
              </div>
            </div>
            <div className={themeData.classes.imageDivClass}>
              <CTALinkWrapper link={props.fields?.primaryImageLink}>
                <MediaPrimary
                  {...props}
                  imageLayout="intrinsic"
                  maxH={'h-full'}
                  staticProps={componentProps?.mediaPrimary}
                />
              </CTALinkWrapper>
            </div>
          </div>
        </div>
      </div>
    </Component>
  );
};
export const getStaticProps: GetStaticComponentProps = async (rendering) => {
  const datasource = rendering as PromoGenericProps;

  const mediaStaticProps: MediaPrimaryStaticProps = {};

  if (!datasource) {
    return mediaStaticProps;
  }

  mediaStaticProps.mediaPrimary = await getMediaPrimaryStaticProps(datasource);

  return mediaStaticProps;
};

export default withDatasourceCheck()<PromoGenericProps>(PromoGeneric);
